import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderQuery } from '@modules/order/state/order-query';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { ISectionVisibilityService } from '@shared/models/section-visibility';

export interface SurgicalGuideFieldsVisibility {
	/** Procedure type dropdown — hidden via template.ui.implantPlanning.items.procedureType.hidden. */
	procedureType: boolean;
}

@Injectable({ providedIn: 'root' })
export class SurgicalGuideVisibilityService implements ISectionVisibilityService<SurgicalGuideFieldsVisibility> {
	readonly fields$: Observable<SurgicalGuideFieldsVisibility> = this.rxTemplateService.template$.pipe(
		map(template => SurgicalGuideVisibilityService.fieldsFromTemplate(template)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private readonly isSectionNonEmpty$: Observable<boolean> = this.fields$.pipe(map(f => f.procedureType));

	readonly isSectionVisible$: Observable<boolean> = combineLatest([this.orderQuery.procedureMap$, this.isSectionNonEmpty$]).pipe(
		map(([procedureMap, nonEmpty]) => procedureMap?.ProcedureId === ProcedureEnum.ImplantPlanning && nonEmpty),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	static fieldsFromTemplate(template: Readonly<RxTemplate>): SurgicalGuideFieldsVisibility {
		return {
			procedureType: !template.ui.implantPlanning?.items.procedureType?.hidden
		};
	}

	constructor(
		private readonly rxTemplateService: RxTemplateService,
		private readonly orderQuery: OrderQuery
	) {}

	fieldsFor$(targetTemplate: Readonly<RxTemplate>, targetProcedureMap: ProcedureMap | null): Observable<SurgicalGuideFieldsVisibility> {
		// If the target procedure map is not ImplantPlanning, procedureType will be hidden.
		if (targetProcedureMap?.ProcedureId !== ProcedureEnum.ImplantPlanning) {
			return of({ procedureType: false });
		}

		return of(SurgicalGuideVisibilityService.fieldsFromTemplate(targetTemplate));
	}
}
